import { waitForTransactionReceipt, WriteContractParameters } from '@wagmi/core';
import invariant from 'invariant';
import { useCallback, useEffect, useState } from 'react';
import { Address, encodeFunctionData } from 'viem';
import { useConnection, useSendCalls, useWaitForCallsStatus, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';

import { config } from '@/app/providers/app/wagmiConfig';
import { useCheckBatching } from '@/app/shared/hooks/blockchain/useCheckBatching';
import { handleTransactionError } from '@/app/shared/hooks/blockchain/useSendTransactions/handleTransactionError';
import { TransactionStatus } from '@/app/shared/hooks/blockchain/useSendTransactions/types';

export interface UseSendBatchTransactionsArgs {
  transactions: WriteContractParameters[];
  enabled?: boolean;
}

export interface UseSendBatchTransactionsReturn {
  txHash: Address | undefined;
  handleSend: () => Promise<void>;
  transactionStatusData: TransactionStatus;
  isBatchingTransaction: boolean;
  errorMessage: string;
  resetTransactionState: () => void;
}

export const useSendBatchTransactions = ({
  transactions,
  enabled = true,
}: UseSendBatchTransactionsArgs): UseSendBatchTransactionsReturn => {
  const { isConnected, address: account } = useConnection();
  const [errorMessage, setErrorMessage] = useState('');
  const [sequentialHash, setSequentialHash] = useState<Address | undefined>();
  const [isSequentialComplete, setIsSequentialComplete] = useState(false);

  const isSupportsBatching = useCheckBatching();
  const isBatchingTransaction = isSupportsBatching && transactions.length > 1;

  const {
    data: txHash,
    mutateAsync: writeContract,
    isPending: isPendingSendTransaction,
    isError: isSendTransactionError,
    reset: resetSendTransaction,
  } = useWriteContract();

  const {
    data: txReceiptData,
    isError: isWaitForTransactionError,
    isLoading: isWaitForTransactionLoading,
    error: waitForTransactionError,
  } = useWaitForTransactionReceipt({
    hash: isBatchingTransaction ? undefined : sequentialHash ?? txHash,
  });

  const {
    data: txBatchData,
    mutateAsync: sendCalls,
    reset: resetSendCalls,
    isError: isErrorSendCalls,
    isPending: isPendingSendCalls,
  } = useSendCalls();

  const {
    data: sendCallsTxReceiptData,
    isLoading: isSendCallsWaiting,
    isSuccess: isSendCallsSuccess,
    isError: isSendCallsErrorWaiting,
    error: sendCallsError,
  } = useWaitForCallsStatus({
    id: txBatchData?.id,
  });

  useEffect(() => {
    if (sendCallsError || waitForTransactionError) {
      const error = sendCallsError || waitForTransactionError;
      const errorMessage = handleTransactionError({
        error,
        action: isBatchingTransaction ? 'batch_wait' : 'transaction_wait',
        isBatching: isBatchingTransaction,
        account,
      });
      setErrorMessage(errorMessage);
    }
  }, [sendCallsError, waitForTransactionError, isBatchingTransaction, account]);

  const resetTransactionState = useCallback(() => {
    resetSendTransaction();
    resetSendCalls();
    setSequentialHash(undefined);
    setIsSequentialComplete(false);
    setErrorMessage('');
  }, [resetSendTransaction, resetSendCalls]);

  const handleSend = async () => {
    if (!enabled || transactions.length === 0) return;

    try {
      setErrorMessage('');
      setIsSequentialComplete(false);
      invariant(isConnected && account, 'Wallet is not connected');

      if (isBatchingTransaction) {
        const callsData = transactions.map((param) => ({
          to: param.address,
          data: encodeFunctionData({
            abi: param.abi,
            functionName: param.functionName,
            args: param.args ?? [],
          }),
          value: param.value,
        }));

        await sendCalls({ calls: callsData });
        return;
      }

      let lastHash: Address | undefined;
      for (const transaction of transactions) {
        lastHash = await writeContract(transaction);
        setSequentialHash(lastHash);
        await waitForTransactionReceipt(config, { hash: lastHash });
      }
      setIsSequentialComplete(true);
    } catch (error) {
      console.error(error);
      setIsSequentialComplete(false);
      setErrorMessage(
        handleTransactionError({
          error,
          action: 'send_transaction',
          isBatching: isBatchingTransaction,
          account,
        }),
      );
    }
  };

  const sendCallsData: TransactionStatus = {
    isLoading: isPendingSendCalls || isSendCallsWaiting,
    isError: isErrorSendCalls || isSendCallsErrorWaiting,
    isSuccess: isSendCallsSuccess,
  };

  const sendTransactionsData: TransactionStatus = {
    isLoading: isPendingSendTransaction || isWaitForTransactionLoading,
    isError: isSendTransactionError || isWaitForTransactionError || !!errorMessage,
    isSuccess: isSequentialComplete,
  };

  const transactionStatusData = isBatchingTransaction ? sendCallsData : sendTransactionsData;

  const lastTxHashSendCall: Address | undefined =
    sendCallsTxReceiptData?.receipts && sendCallsTxReceiptData.receipts.length > 0
      ? sendCallsTxReceiptData.receipts[sendCallsTxReceiptData.receipts.length - 1]?.transactionHash
      : undefined;

  const transactionHash = isBatchingTransaction ? lastTxHashSendCall : txReceiptData?.transactionHash;

  return {
    txHash: transactionHash,
    isBatchingTransaction,
    handleSend,
    transactionStatusData,
    errorMessage,
    resetTransactionState,
  };
};
